import React from 'react';
import { useNavigate } from 'react-router-dom';

function useReveal() {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            obs.unobserve(e.target);
          }
        }),
      { threshold: 0.15 }
    );
    el.querySelectorAll('.reveal').forEach((c) => obs.observe(c));
    return () => obs.disconnect();
  }, []);
  return ref;
}

const Design3D: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useReveal();
  const [scrolledPastHero, setScrolledPastHero] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => {
      setScrolledPastHero(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goBack = () => {
    setLeaving(true);
    setTimeout(() => navigate('/bookshelf'), 450);
  };

  return (
    <div ref={containerRef} className="page-enter" style={{ minHeight: '100vh', background: '#fff', overflowX: 'hidden', opacity: leaving ? 0 : 1, transition: 'opacity 0.45s ease' }}>

      <button
        onClick={goBack}
        style={{
          position: 'fixed', top: 28, left: 28, zIndex: 20,
          font: "300 10px/1 'Lato', sans-serif", letterSpacing: '0.3em',
          color: scrolledPastHero ? '#1a1a1a' : 'rgba(255,255,255,0.7)',
          border: 'none', background: 'none', cursor: 'pointer', transition: 'color 0.4s ease',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = scrolledPastHero ? '#d4af37' : '#fff')}
        onMouseLeave={(e) => (e.currentTarget.style.color = scrolledPastHero ? '#1a1a1a' : 'rgba(255,255,255,0.7)')}
      >
        &#8592; RETURN TO THE SHELF
      </button>

      {/* Hero */}
      <section style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <img
          src="https://lh3.googleusercontent.com/d/1DKyydGuE1gRSj48SjNTR30aFl_rwTqmy"
          alt="3-D Design"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 100%)' }} />
        <div style={{ position: 'absolute', bottom: 'clamp(60px, 12vh, 120px)', left: 'clamp(28px, 5vw, 80px)' }}>
          <h1 style={{ font: "italic 300 clamp(36px, 5.5vw, 72px)/1.1 'Playfair Display', serif", color: '#fff', margin: 0 }}>
            3-D Design
          </h1>
          <p style={{ font: "italic 300 clamp(12px, 1.1vw, 16px)/1.6 'Playfair Display', serif", letterSpacing: '0.06em', color: 'rgba(255,255,255,0.7)', marginTop: 16, maxWidth: 500 }}>
            Spaces built to be remembered.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="reveal" style={{ padding: 'clamp(60px, 10vh, 120px) clamp(24px, 6vw, 120px)', maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
        <p style={{ font: "300 clamp(16px, 1.3vw, 20px)/1.85 'Lato', sans-serif", color: '#3a3a3a' }}>
          Some moments deserve to be built, not just imagined. RBC Creative designs the physical world a brand lives in — fixtures, displays, pop-ups, and full environments rendered in precise three dimensions before a single material is cut. You see it, walk it, and approve it before it exists. What gets built is exactly what was promised.
        </p>
      </section>

      {/* Approach heading */}
      <section className="reveal" style={{ textAlign: 'center', padding: '0 clamp(24px, 6vw, 120px) clamp(32px, 4vh, 48px)' }}>
        <h2 style={{ font: "300 11px/1 'Lato', sans-serif", letterSpacing: '0.4em', color: '#d4af37', margin: 0, textTransform: 'uppercase' }}>
          What we build
        </h2>
      </section>

      {/* Approach cards */}
      <section className="reveal" style={{ padding: '0 clamp(24px, 6vw, 120px) clamp(60px, 10vh, 120px)', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'clamp(32px, 3vw, 64px)' }}>
          <div>
            <span style={{ font: "300 11px/1 'Lato', sans-serif", letterSpacing: '0.3em', color: '#d4af37' }}>01</span>
            <h3 style={{ font: "italic 24px/1.3 'Playfair Display', serif", color: '#1a1a1a', margin: '12px 0 8px' }}>Fixtures &amp; Display Systems</h3>
            <p style={{ font: "300 14px/1.75 'Lato', sans-serif", color: '#666' }}>
              Custom plinths, units, and modular systems designed to flatter the product and outlast the season — beautiful objects that happen to do a job.
            </p>
          </div>
          <div>
            <span style={{ font: "300 11px/1 'Lato', sans-serif", letterSpacing: '0.3em', color: '#d4af37' }}>02</span>
            <h3 style={{ font: "italic 24px/1.3 'Playfair Display', serif", color: '#1a1a1a', margin: '12px 0 8px' }}>Pop-Ups &amp; Activations</h3>
            <p style={{ font: "300 14px/1.75 'Lato', sans-serif", color: '#666' }}>
              Temporary spaces with permanent impact. We design activations engineered for footfall, for the photograph, and for the feeling people carry out the door.
            </p>
          </div>
          <div>
            <span style={{ font: "300 11px/1 'Lato', sans-serif", letterSpacing: '0.3em', color: '#d4af37' }}>03</span>
            <h3 style={{ font: "italic 24px/1.3 'Playfair Display', serif", color: '#1a1a1a', margin: '12px 0 8px' }}>Environmental Concepts</h3>
            <p style={{ font: "300 14px/1.75 'Lato', sans-serif", color: '#666' }}>
              The whole room as a single idea. We develop spatial concepts and detailed 3-D visualizations that align every stakeholder long before the build begins.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy pull quote */}
      <section className="reveal" style={{ padding: 'clamp(40px, 6vh, 80px) clamp(24px, 6vw, 120px)', maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
        <blockquote style={{ font: "italic 300 clamp(20px, 2.5vw, 32px)/1.5 'Playfair Display', serif", color: '#1a1a1a', margin: 0, borderLeft: '2px solid #d4af37', paddingLeft: 'clamp(20px, 3vw, 40px)' }}>
          Designed in detail. Delivered without surprises.
        </blockquote>
      </section>

      {/* Process */}
      <section className="reveal" style={{ padding: 'clamp(40px, 6vh, 80px) clamp(24px, 6vw, 120px)', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'clamp(24px, 3vw, 48px)' }}>
          <div>
            <h4 style={{ font: "italic 18px/1.3 'Playfair Display', serif", color: '#d4af37', margin: '0 0 8px' }}>Concept</h4>
            <p style={{ font: "300 14px/1.75 'Lato', sans-serif", color: '#666' }}>
              We sketch the spatial idea and the feeling it should produce.
            </p>
          </div>
          <div>
            <h4 style={{ font: "italic 18px/1.3 'Playfair Display', serif", color: '#d4af37', margin: '0 0 8px' }}>Visualize</h4>
            <p style={{ font: "300 14px/1.75 'Lato', sans-serif", color: '#666' }}>
              We render it in three dimensions so you approve reality, not a promise.
            </p>
          </div>
          <div>
            <h4 style={{ font: "italic 18px/1.3 'Playfair Display', serif", color: '#d4af37', margin: '0 0 8px' }}>Specify</h4>
            <p style={{ font: "300 14px/1.75 'Lato', sans-serif", color: '#666' }}>
              We document materials, dimensions, and finishes for a clean build.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="reveal" style={{ maxWidth: 80, height: 1, background: '#d4af37', margin: '0 auto' }} />

      {/* CTA */}
      <section className="reveal" style={{ padding: 'clamp(60px, 10vh, 120px) clamp(24px, 6vw, 120px)', textAlign: 'center' }}>
        <h2 style={{ font: "italic 300 clamp(24px, 3vw, 40px)/1.3 'Playfair Display', serif", color: '#1a1a1a', margin: 0 }}>
          Let's build something worth remembering.
        </h2>
        <a
          href="mailto:hello@rbccreative.com"
          style={{ display: 'inline-block', marginTop: 28, font: "300 11px/1 'Lato', sans-serif", letterSpacing: '0.3em', color: '#1a1a1a', borderBottom: '1px solid #d4af37', paddingBottom: 4, textDecoration: 'none' }}
        >
          BEGIN A CONVERSATION
        </a>
      </section>

      <footer style={{ padding: '28px 40px 40px', textAlign: 'center' }}>
        <p style={{ font: "300 10px/1.6 'Lato', sans-serif", letterSpacing: '0.2em', color: '#999', margin: 0 }}>
          RBC CREATIVE — VISUAL MERCHANDISING · 3-D DESIGN · BRANDING
        </p>
        <p style={{ font: "300 9px/1 'Lato', sans-serif", letterSpacing: '0.15em', color: '#bbb', marginTop: 8 }}>
          © RBC Creative. Composed with intention.
        </p>
      </footer>
    </div>
  );
};

export default Design3D;
